import { injectLocal } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { computed, type Ref } from 'vue'

import { type Brand } from '@/core/types'
import { logError } from '@/logger'
import { OlympusClient } from '@/OlympusClient'
import { isLocalDev } from '@/utils/environment/isLocalDev'
import { useReactiveFeatureFlag } from '@/utils/useFeatureFlags'

import { ACTIVE_BRAND_CONTEXT_KEY } from '../constants'
import { type CoreStoreBrand, useCoreStore } from '../useCoreStore'

/**
 * Returns the current active brand from the store
 */
export const useActiveBrand = () => {
  /**
   * If the ACTIVE_BRAND_CONTEXT_KEY is set, that means we are inside the
   * ActiveBrandProvider and that we definitely have data in the store. This allows us
   * to ignore the potential of an undefined value for activeBrandId and activeBrand
   */
  if (!injectLocal(ACTIVE_BRAND_CONTEXT_KEY)) {
    // tmp feature flag just to monitor evaluations and be able to turn off logging/erroring just in case
    const { enabled: logOrThrowErrors } = useReactiveFeatureFlag(
      'hermes.checkActiveBrandContext'
    )
    if (logOrThrowErrors.value) {
      const error =
        'should not use useActiveBrand outside of the active brand context'
      logError(error, { context: 'useActiveBrand', message: error })
      // throw error if in local dev for more visibility
      if (isLocalDev()) {
        throw new Error(error)
      }
    }
  }

  const { activeBrand: piniaActiveBrand, activeBrandMergedTheme: mergedTheme } =
    storeToRefs(useCoreStore())
  const { updateActiveBrandData } = useCoreStore()

  const activeBrand = computed(
    () => (piniaActiveBrand as Ref<CoreStoreBrand>).value // casting activeBrand as defined due to the active brand context
  )

  const activeBrandId = computed(() => activeBrand.value.id)
  const domain = computed(() => activeBrand.value.domain)
  const name = computed(() => activeBrand.value.name)
  const links = computed(() => activeBrand.value.links)
  const logo = computed(() => activeBrand.value.assets.logo)
  const highestSubscriptionTier = computed(
    () => activeBrand.value.metadata.highestSubscriptionTier
  )

  const locations = computed(() => activeBrand.value.locations ?? [])
  const isMultiLocation = computed(() => locations.value.length > 1)
  const isSingleLocation = computed(() => locations.value.length === 1)
  const locationsWithoutStripeConnected = computed(() =>
    locations.value.filter((location) => !location.processing.stripe?.id)
  )
  const locationsWithoutYextConnected = computed(() =>
    locations.value.filter((location) => !location.listingsUpdatedAt)
  )

  const getLocationById = computed(() => {
    return (locationId: string) =>
      locations.value.find((loc) => loc.id === locationId)
  })

  const getLocationNameById = computed(() => {
    return (locationId: string) => getLocationById.value(locationId)?.name ?? ''
  })

  const getLocationPosById = computed(() => {
    return (locationId: string | undefined) =>
      locationId
        ? (getLocationById.value(locationId)?.pos ?? undefined)
        : undefined
  })

  const theme = computed(() => activeBrand.value.theme)

  const updateBrand = async ({
    brandId,
    update,
  }: {
    brandId: string
    update: Partial<Brand>
  }) => {
    const brand = await OlympusClient.brandsV0.brand.updateBrand({
      body: update,
      params: {
        brandId,
      },
    })

    updateActiveBrandData(brand)
  }

  const updateDomain = async ({ domain }: { domain: string | null }) => {
    updateActiveBrandData({ ...activeBrand.value, domain })
  }

  return {
    activeBrand,
    activeBrandId,
    domain,
    getLocationById,
    getLocationNameById,
    getLocationPosById,
    highestSubscriptionTier,
    isMultiLocation,
    isSingleLocation,
    links,
    locations,
    locationsWithoutStripeConnected,
    locationsWithoutYextConnected,
    logo,
    mergedTheme,
    name,
    theme,
    updateActiveBrandData,
    updateBrand,
    updateDomain,
  }
}
